import clsx from 'clsx';
import { Fragment } from 'react';

import { LegacyColorMap } from '@/theme/LegacyColorMap';
import { Colors } from '../../../../theme';
import type { AnchorTarget } from '../../../../ts-types';
import { Heading } from '../../../display/Heading';
import type { IconName } from '../../../display/Icon';
import { Icon } from '../../../display/Icon';
import { Tag } from '../../../display/Tag';
import { Text } from '../../../display/Text';
import { Grid } from '../../../layout/Grid';
import { LegacyButton } from '../../../navigation/LegacyButton';
import { SubscriptionFormWithDisclaimer } from '../../SubscriptionForm';
import { HeroColorsV4, HeroColorsV5 } from '../types';
import {
  getSupportedCTAColor,
  transformBackgroundColor,
  validateZapierBackgroundAccessibility,
} from '../utils';

import styles from './HeroColorBlock.module.scss';

export type Props = {
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'lava60' | 'ocean40' | 'sun60' | 'white' | 'zapier' | 'night' | 'moss' | 'neutral800'`.
   * */
  backgroundColor?: HeroColorsV4 | Exclude<HeroColorsV5, 'neutral100'>;
  /**
   * Heading text content.
   */
  heading: React.ReactNode;
  /**
   * Text content displayed below the heading.
   */
  description?: React.ReactNode;
  /**
   * Whether the subscription form is enabled in place of CTA button.
   */
  isSubscriptionForm?: boolean;
  /**
   * Placeholder text to display inside the subscription form input field.
   */
  subscriptionPlaceholder?: string;
  /**
   * Background color of the CTA button.
   */
  ctaColor?: 'primary-alt' | 'primary-dark' | 'brand';
  /**
   * Text content of the CTA button.
   */
  ctaText?: React.ReactNode;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * The `target` attribute for the CTA button.
   */
  ctaTarget?: AnchorTarget;
  /**
   * Whether the CTA button is disabled
   */
  ctaDisabled?: boolean;
  /**
   * Text displayed next to CTA button.
   */
  ctaDisclaimer?: React.ReactNode;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * Renders in the space opposite the text.
   * Can be used to render media such as images, videos, or any other React Component. */
  children?: React.ReactNode;
  /**
   * Function that gets called on subscription form submit.
   */
  onSubscriptionFormSubmit?: (subscriptionValue?: string) => void;
  /**
   * A label that renders as a small tag
   * with a background color and an icon
   */
  label?: string;
  /**
   * The icon that is displayed in the label.
   */
  labelIcon?: IconName;
  /**
   * The labelColor determines the background-color of the label.
   */
  labelColor?:
    | 'default'
    | 'success'
    | 'error'
    | 'warning'
    | 'inverted'
    | 'simple';
};

/**
 * `HeroColorBlock` is used as a large promotional section towards the top of a page. It features a heading, description text, a Call To Action (CTA), and media, which is rendered as `children`
 */
export const HeroColorBlock = ({
  ctaColor = 'primary-alt',
  isSubscriptionForm = false,
  subscriptionPlaceholder = 'Email address',
  backgroundColor = 'zapier',
  heading,
  description,
  ctaText,
  ctaHref,
  ctaTarget,
  ctaDisabled = false,
  ctaDisclaimer,
  onCtaClick,
  onCtaKeyDown,
  children,
  onSubscriptionFormSubmit,
  label,
  labelIcon,
  labelColor,
}: Props) => {
  const mappedBackgroundColor = transformBackgroundColor(backgroundColor);

  // This should validate and through a console error as the disclaimer text accessibility
  // is poor on the zapier background.
  validateZapierBackgroundAccessibility(
    mappedBackgroundColor,
    isSubscriptionForm,
    !!ctaDisclaimer
  );

  const getCtaComponent = () => {
    const color = getSupportedCTAColor(ctaColor, mappedBackgroundColor);
    return (
      <LegacyButton
        children={ctaText}
        href={!isSubscriptionForm ? ctaHref : undefined}
        onClick={onCtaClick}
        onKeyDown={onCtaKeyDown}
        size={isSubscriptionForm ? 'medium' : 'large'}
        type={isSubscriptionForm ? 'submit' : 'button'}
        data-testid={'color-block-hero-cta-button'}
        target={ctaTarget}
        disabled={ctaDisabled}
        // "primary-alt" no longer exists as of V8 and is now mapped to "primary"
        color={color === 'primary-alt' ? 'primary' : color}
      />
    );
  };

  const disclaimerColor =
    mappedBackgroundColor === 'zapier' ? 'BrandCream' : 'PrimeWhite';

  return (
    <div
      className={styles.root}
      style={{
        '--_zds-hero-color-gradient1':
          Colors[LegacyColorMap[mappedBackgroundColor]],
        '--_zds-hero-color-gradient2': Colors.PrimeWhite,
        '--_zds-hero-color-heading':
          Colors[
            mappedBackgroundColor === 'zapier' ? 'BrandCream' : 'PrimeWhite'
          ],
        '--_zds-hero-color-description':
          Colors[
            mappedBackgroundColor === 'zapier' ? 'BrandCream' : 'PrimeWhite'
          ],
        '--_zds-hero-color-disclaimer': disclaimerColor,
      }}
      data-background-color={mappedBackgroundColor}
      data-zds
    >
      <div className={styles.content} data-zds>
        {label && (
          <div className={styles.labelContainer} data-zds>
            <Tag variant={labelColor}>
              {labelIcon ? (
                <Grid alignItems="center" columns="auto max-content" gap={5}>
                  <Icon name={labelIcon} size={20} />
                  {label}
                </Grid>
              ) : (
                label
              )}
            </Tag>
          </div>
        )}
        <Heading className={styles.heading}>{heading}</Heading>
        {description && (
          <div className={styles.description} data-zds>
            {description}
          </div>
        )}
        {ctaText && (ctaHref || onCtaClick || isSubscriptionForm) && (
          <div
            className={clsx(
              styles.ctaContainer,
              isSubscriptionForm && styles.subscriptionForm
            )}
            data-zds
          >
            {isSubscriptionForm ? (
              <SubscriptionFormWithDisclaimer
                placeholderText={subscriptionPlaceholder}
                onSubmit={onSubscriptionFormSubmit}
                renderButton={() => getCtaComponent()}
                disclaimerTextColor={
                  mappedBackgroundColor === 'neutral100'
                    ? 'primary'
                    : 'secondary'
                }
              />
            ) : (
              <Fragment>
                <div className={styles.ctaButtonWrapper} data-zds>
                  {getCtaComponent()}
                </div>
                {ctaDisclaimer && (
                  <div
                    className={clsx(
                      styles.ctaDisclaimer,
                      isSubscriptionForm && styles.subscriptionFormDisclaimer
                    )}
                    data-zds
                  >
                    <Text tag="div" type="Body1" color={disclaimerColor}>
                      {ctaDisclaimer}
                    </Text>
                  </div>
                )}
              </Fragment>
            )}
          </div>
        )}
        <div className={styles.mediaContent} data-zds>
          {children}
        </div>
      </div>
    </div>
  );
};
